'use client';

import { Button } from '@/components/ui/button';
import { useToast } from '../ui/use-toast';
import { ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import {
  checkoutPayment,
  Order,
  paymentEmailNotify,
  verifyPayment,
} from '@/actions/processPayment.actions';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PaymentButtonProps {
  plan: 'PRO' | 'PREMIUM';
  className?: string;
  icon?: ReactNode;
}

//TODO: Figerout how to handle multiple subscription plans
export function PaymentButton({ plan, className, icon }: PaymentButtonProps) {
  const { toast } = useToast();
  const router = useRouter();
  const { data: session, status } = useSession();

  const handlePayment = async () => {
    // Check if user is authenticated
    if (status === 'unauthenticated') {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to continue with the payment.',
        variant: 'destructive',
      });
      router.push('/signin');
      return;
    }

    try {
      //TODO: move all this api calls to server actions for security
      const userDetails = {
        userName: session?.user?.name,
        userEmail: session?.user?.email,
        userId: session?.user?.id,
      };
      const checkoutDetails = (await checkoutPayment(
        plan,
        userDetails
      )) as Order;

      // Initialize Razorpay
      const options = {
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
        amount: checkoutDetails.order.amount,
        currency: checkoutDetails.order.currency,
        name: 'JobAssure',
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan Subscription`,
        order_id: checkoutDetails.order.id,
        handler: async (response: any) => {
          try {
            // Verify payment
            const paymentVerified = await verifyPayment(response, plan);

            if (paymentVerified?.status === 200) {
              toast({
                title: 'Success!',
                description: 'Your payment has been processed successfully.',
                variant: 'success',
              });

              // Redirect to dashboard or success page
              window.location.href = '/jobs';

              await paymentEmailNotify(
                userDetails.userEmail as string,
                'PAYMENT_SUCCESSFULL'
              );
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast({
              title: 'Error',
              description: 'There was an error verifying your payment.',
              variant: 'destructive',
            });

            await paymentEmailNotify(
              userDetails.userEmail as string,
              'PAYMENT_FAILED'
            );
          }
        },
        prefill: {
          name: session?.user?.name || 'User Name',
          email: session?.user?.email || 'user@example.com',
          contact: '9999999999',
        },
        //TODO: elevate the payment page  https://razorpay.com/docs/payments/payment-gateway/emi%C2%B2/widget/native-web/customise/#3-themes-and-colours
        theme: {
          color: '#4F46E5',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Error',
        description: 'There was an error processing your payment.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button onClick={handlePayment} className={className}>
      <div className="flex items-center gap-2">
        {icon}
        Upgrade to {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </div>
    </Button>
  );
}
